import React, { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { useStore } from '../../store';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
} from '@roger/ui-components';
import { API_BASE_URL } from '../../services/api';
import { downloadResponseAsFile } from '../../utils/downloadFile';
import RadioButtonFormSection from './RadioButtonFormSection';
import {
  MEDICATION_FORM_QUESTIONS,
  MEDICATION_REVIEW_DISCLAIMER,
  DIGITAL_SIGNATURE_CONFIRMATION,
  YesNoNaValue,
  YesNoValue,
} from './medicationFormConstants';

interface MedicationRecordModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  onDocumentCreated?: () => void;
}

const MedicationRecordModal: React.FC<MedicationRecordModalProps> = ({
  open,
  onOpenChange,
  patientId,
  onDocumentCreated,
}) => {
  const currentUser = useStore(state => state.user);
  const signerDisplay = currentUser?.name || currentUser?.email || 'Unknown';
  const [physicianContacted, setPhysicianContacted] = useState<
    YesNoNaValue | ''
  >('');
  const [physicianContactedExplanation, setPhysicianContactedExplanation] =
    useState('');

  const [actionTaken, setActionTaken] = useState<YesNoValue | ''>('');
  const [actionTakenExplanation, setActionTakenExplanation] = useState('');

  const [interactionsNotified, setInteractionsNotified] = useState<
    YesNoNaValue | ''
  >('');
  const [interactionsNotifiedExplanation, setInteractionsNotifiedExplanation] =
    useState('');

  const [digitalSignatureConfirmed, setDigitalSignatureConfirmed] =
    useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    setIsGenerating(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/patients/${patientId}/medications/pdf`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            physician_contacted: physicianContacted,
            physician_contacted_explanation: physicianContactedExplanation,
            action_taken: actionTaken,
            action_taken_explanation: actionTakenExplanation,
            interactions_notified: interactionsNotified,
            interactions_notified_explanation: interactionsNotifiedExplanation,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate PDF: ${response.statusText}`);
      }

      await downloadResponseAsFile(response, 'medication_record.pdf');

      // Close modal and reset form
      handleClose();
      onDocumentCreated?.();
    } catch (error) {
      console.error('Failed to generate medication record:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleClose = () => {
    if (!isGenerating) {
      onOpenChange(false);
      // Reset form
      setPhysicianContacted('');
      setPhysicianContactedExplanation('');
      setActionTaken('');
      setActionTakenExplanation('');
      setInteractionsNotified('');
      setInteractionsNotifiedExplanation('');
      setDigitalSignatureConfirmed(false);
    }
  };

  const isFormValid =
    physicianContacted !== '' &&
    actionTaken !== '' &&
    interactionsNotified !== '' &&
    digitalSignatureConfirmed;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Generate Patient Medication Record</DialogTitle>
          <DialogDescription>
            Please complete the following information before generating the
            medication record.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-6 py-4'>
          {/* Physician Contacted */}
          <RadioButtonFormSection
            label={MEDICATION_FORM_QUESTIONS.PHYSICIAN_CONTACTED.label}
            htmlFor='physician-contacted'
            value={physicianContacted}
            onChange={val => setPhysicianContacted(val as YesNoNaValue)}
            options={MEDICATION_FORM_QUESTIONS.PHYSICIAN_CONTACTED.options}
            explanationValue={physicianContactedExplanation}
            onExplanationChange={setPhysicianContactedExplanation}
          />

          {/* Action Taken */}
          <RadioButtonFormSection
            label={MEDICATION_FORM_QUESTIONS.ACTION_TAKEN.label}
            htmlFor='action-taken'
            value={actionTaken}
            onChange={val => setActionTaken(val as YesNoValue)}
            options={MEDICATION_FORM_QUESTIONS.ACTION_TAKEN.options}
            explanationValue={actionTakenExplanation}
            onExplanationChange={setActionTakenExplanation}
          />

          {/* Interactions Notified */}
          <RadioButtonFormSection
            label={MEDICATION_FORM_QUESTIONS.INTERACTIONS_NOTIFIED.label}
            htmlFor='interactions-notified'
            value={interactionsNotified}
            onChange={val => setInteractionsNotified(val as YesNoNaValue)}
            options={MEDICATION_FORM_QUESTIONS.INTERACTIONS_NOTIFIED.options}
            explanationValue={interactionsNotifiedExplanation}
            onExplanationChange={setInteractionsNotifiedExplanation}
          />

          {/* Digital Signature */}
          <div className='border-t pt-4 space-y-3'>
            <p className='text-sm text-muted-foreground'>
              {MEDICATION_REVIEW_DISCLAIMER}
            </p>
            <div className='flex items-start space-x-2'>
              <Checkbox
                id='digital-signature'
                checked={digitalSignatureConfirmed}
                onCheckedChange={checked =>
                  setDigitalSignatureConfirmed(checked === true)
                }
                className='mt-0.5'
              />
              <div className='space-y-1'>
                <Label
                  htmlFor='digital-signature'
                  className='text-sm font-medium cursor-pointer leading-tight'
                >
                  {DIGITAL_SIGNATURE_CONFIRMATION}
                </Label>
                <p className='text-xs text-muted-foreground'>
                  Signed by: <strong>{signerDisplay}</strong>
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={isGenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={!isFormValid || isGenerating}
          >
            {isGenerating && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
            <FileText className='h-4 w-4 mr-2' />
            Generate PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MedicationRecordModal;
